/**
 * Create / edit a payment link. One scrolling form in five sections with a live preview of the
 * payer page, then (on create) a success step with the short link and QR code.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  CalendarClock,
  Check,
  ChevronDown,
  Copy,
  CreditCard,
  Download,
  Eye,
  Landmark,
  Loader2,
  Mail,
  MessageCircle,
  RefreshCw,
  Share2,
  X,
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { TagInput } from '@/components/ui/tag-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { PaymentLink, PaymentLinkAmountType, PaymentLinkConfigInput, PaymentLinkMethod } from '@masonic-tools/shared-types';
import { PAYMENT_LINK_METHODS } from '@masonic-tools/shared-types';
import {
  DESCRIPTION_MAX_LENGTH,
  emptyFormInput,
  expiryPreset,
  feeExampleText,
  formatPence,
  formatPenceShort,
  fromDateTimeLocalValue,
  GIFT_AID_DECLARATION_TEMPLATE,
  linkToFormInput,
  MAX_SUGGESTED_AMOUNTS,
  METHOD_DESCRIPTIONS,
  METHOD_LABELS,
  minimumAmountForLink,
  normalizeReference,
  parsePoundsInput,
  penceToInput,
  REFERENCE_MAX_LENGTH,
  THANK_YOU_MAX_LENGTH,
  TITLE_MAX_LENGTH,
  toDateTimeLocalValue,
  validatePaymentLinkForm,
  type FormErrors,
} from '@/lib/unit-payment-links';
import { callableErrorInfo, createPaymentLink, retryPaymentLinkShortUrl, updatePaymentLink } from '@/lib/payment-links-api';
import type { PaymentLinkSetup } from '@/hooks/usePaymentLinks';
import { PublicPaymentLinkCard } from './PublicPaymentLinkCard';
import { copyLink, useQrPreview, usePaymentLinkDownloads } from './PaymentLinkQrDialog';
import { showError, showSuccess } from '@/lib/swal';

interface Props {
  unitId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  setup: PaymentLinkSetup;
  /** Pass a link to edit it; leave out to create a new one. */
  link?: PaymentLink | null;
  onSaved?: (linkId: string) => void;
}

const SERVER_FIELD_MAP: Record<string, string> = {
  'notifications.extraRecipients': 'extraRecipients',
};

const AMOUNT_TYPES: Array<{ value: PaymentLinkAmountType; label: string }> = [
  { value: 'fixed', label: 'Fixed' },
  { value: 'range', label: 'Range' },
  { value: 'open', label: 'Any amount' },
];

const METHOD_ICONS: Record<PaymentLinkMethod, typeof CreditCard> = { stripe: CreditCard, sumup: CreditCard, slickpay: Landmark };

function Section({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-4 border-b border-border pb-6 last:border-b-0" aria-labelledby={`pl-form-s${n}`}>
      <h3 id={`pl-form-s${n}`} className="flex items-center gap-2 text-base font-semibold">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">{n}</span>
        {title}
      </h3>
      {children}
    </section>
  );
}

function FieldError({ message }: { message?: string }) {
  return message ? (
    <p className="mt-1 text-sm text-destructive" role="alert">
      {message}
    </p>
  ) : null;
}

function MoneyInput({
  id,
  label,
  value,
  onChange,
  error,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (text: string) => void;
  error?: string;
}) {
  return (
    <div className="min-w-0 flex-1">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
        <Input id={id} inputMode="decimal" className="pl-7 tabular-nums" value={value} aria-invalid={!!error} onChange={(e) => onChange(e.target.value)} />
      </div>
      <FieldError message={error} />
    </div>
  );
}

export function PaymentLinkFormDialog({ unitId, open, onOpenChange, setup, link, onSaved }: Props) {
  const navigate = useNavigate();
  const editing = !!link;
  const defaultMethod: PaymentLinkMethod = setup.availableMethods[0] ?? 'stripe';

  const [input, setInput] = useState<PaymentLinkConfigInput>(() => (link ? linkToFormInput(link) : emptyFormInput(defaultMethod)));
  const [fixedText, setFixedText] = useState('');
  const [minText, setMinText] = useState('');
  const [maxText, setMaxText] = useState('');
  const [suggestText, setSuggestText] = useState('');
  const [noExpiry, setNoExpiry] = useState(true);
  const [expiryText, setExpiryText] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [serverError, setServerError] = useState<{ message: string; field?: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const [success, setSuccess] = useState<{ linkId: string; shortUrl: string; shortenerUsed: boolean } | null>(null);
  const [retrying, setRetrying] = useState(false);

  // Reset whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    const start = link ? linkToFormInput(link) : emptyFormInput(defaultMethod);
    setInput(start);
    setFixedText(penceToInput(start.fixedAmountPence));
    setMinText(penceToInput(start.minAmountPence));
    setMaxText(penceToInput(start.maxAmountPence));
    setSuggestText('');
    setNoExpiry(!start.expiresAt);
    setExpiryText(start.expiresAt ? toDateTimeLocalValue(new Date(start.expiresAt)) : '');
    setSubmitAttempted(false);
    setServerError(null);
    setSuccess(null);
    setShowMobilePreview(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, link?.id]);

  // Pick an available method once setup has loaded.
  useEffect(() => {
    if (!editing && setup.availableMethods.length > 0 && !setup.availableMethods.includes(input.paymentMethod)) {
      setInput((i) => ({ ...i, paymentMethod: setup.availableMethods[0] }));
    }
  }, [setup.availableMethods, editing, input.paymentMethod]);

  const set = <K extends keyof PaymentLinkConfigInput>(key: K, value: PaymentLinkConfigInput[K]) => setInput((i) => ({ ...i, [key]: value }));

  const feePence = setup.feePence[input.paymentMethod] ?? 0;
  const methodsForValidation = editing && link ? Array.from(new Set([...setup.availableMethods, link.paymentMethod])) : setup.availableMethods;
  const errors: FormErrors = useMemo(
    () =>
      validatePaymentLinkForm(input, {
        feePence,
        nowMs: Date.now(),
        availableMethods: methodsForValidation,
        existingExpiresAtMs: link?.expiresAt?.getTime() ?? null,
      }),
    [input, feePence, methodsForValidation, link?.expiresAt],
  );
  const serverField = serverError?.field ? SERVER_FIELD_MAP[serverError.field] || serverError.field : undefined;
  const err = (field: string) => (submitAttempted ? errors[field] : undefined) || (serverField === field ? serverError?.message : undefined);

  const exampleAmount = Math.max(minimumAmountForLink(input), input.amountType === 'open' ? 1000 : 0);
  const noMethods = setup.availableMethods.length === 0 && !editing;
  const changedPricing = editing && link && link.paymentCount > 0 && (input.amountType !== link.amountType || input.feeMode !== link.feeMode);

  const addSuggestion = () => {
    const p = parsePoundsInput(suggestText);
    if (p === null) return;
    if (input.suggestedAmountsPence.includes(p) || input.suggestedAmountsPence.length >= MAX_SUGGESTED_AMOUNTS) return;
    set('suggestedAmountsPence', [...input.suggestedAmountsPence, p].sort((a, b) => a - b));
    setSuggestText('');
  };

  const setExpiryFromDate = (d: Date) => {
    const v = toDateTimeLocalValue(d);
    setExpiryText(v);
    setNoExpiry(false);
    set('expiresAt', fromDateTimeLocalValue(v));
  };

  const handleSave = async () => {
    setSubmitAttempted(true);
    setServerError(null);
    if (Object.keys(errors).length > 0) {
      showError('Please fix the highlighted fields');
      return;
    }
    const payload: PaymentLinkConfigInput = {
      ...input,
      reference: normalizeReference(input.reference),
      expiresAt: noExpiry ? null : input.expiresAt,
      fixedAmountPence: input.amountType === 'fixed' ? input.fixedAmountPence : null,
      minAmountPence: input.amountType === 'range' ? input.minAmountPence : null,
      maxAmountPence: input.amountType === 'range' ? input.maxAmountPence : null,
      suggestedAmountsPence: input.amountType === 'fixed' ? [] : input.suggestedAmountsPence,
    };
    setSaving(true);
    try {
      if (editing && link) {
        await updatePaymentLink({ ...payload, unitId, linkId: link.id });
        showSuccess('Payment link saved');
        onSaved?.(link.id);
        onOpenChange(false);
      } else {
        const result = await createPaymentLink({ ...payload, unitId });
        setSuccess({ linkId: result.linkId, shortUrl: result.shortUrl, shortenerUsed: result.shortenerUsed });
        onSaved?.(result.linkId);
      }
    } catch (e) {
      const info = callableErrorInfo(e, 'Could not save the payment link');
      setServerError(info);
      showError(info.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRetryShortLink = async () => {
    if (!success) return;
    setRetrying(true);
    try {
      const r = await retryPaymentLinkShortUrl({ unitId, linkId: success.linkId });
      setSuccess({ ...success, shortUrl: r.shortUrl, shortenerUsed: r.shortenerUsed });
      if (r.shortenerUsed) showSuccess('Short link created');
      else showError(r.error || 'The short link service is unavailable');
    } catch (e) {
      showError(callableErrorInfo(e, 'Could not create the short link').message);
    } finally {
      setRetrying(false);
    }
  };

  const previewLink = {
    id: link?.id || 'preview',
    title: input.title,
    description: input.description,
    reference: normalizeReference(input.reference),
    thankYouMessage: input.thankYouMessage,
    amountType: input.amountType,
    fixedAmountPence: input.fixedAmountPence,
    minAmountPence: input.minAmountPence,
    maxAmountPence: input.maxAmountPence,
    suggestedAmountsPence: input.amountType === 'fixed' ? [] : input.suggestedAmountsPence,
    feeMode: input.feeMode,
    feePence,
    paymentMethod: input.paymentMethod,
    giftAidEnabled: input.giftAidEnabled,
    expiresAtMs: input.expiresAt ? Date.parse(input.expiresAt) : null,
  };
  const preview = (
    <PublicPaymentLinkCard
      key={`${input.amountType}-${input.fixedAmountPence}`}
      unitName={setup.unitName}
      link={previewLink}
      giftAidTemplate={GIFT_AID_DECLARATION_TEMPLATE}
      preview
    />
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[calc(100dvh-2rem)] max-w-6xl flex-col overflow-hidden p-0" showCloseButton={false}>
        <div className="flex items-start justify-between gap-4 border-b border-border px-5 py-4 sm:px-6">
          <div>
            <DialogTitle>{success ? 'Your payment link is ready' : editing ? 'Edit payment link' : 'New payment link'}</DialogTitle>
            <DialogDescription className="mt-1">
              {success ? 'Share the link or QR code to start collecting.' : 'Collect charity donations and other payments with a shareable link or QR code.'}
            </DialogDescription>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-sm opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {success ? (
          <SuccessStep
            unitName={setup.unitName}
            title={input.title}
            reference={normalizeReference(input.reference)}
            success={success}
            retrying={retrying}
            onRetry={handleRetryShortLink}
            onDone={() => {
              onOpenChange(false);
              navigate(`/unit/${unitId}/payment-links/${success.linkId}`);
            }}
            input={input}
          />
        ) : (
          <>
            <div className="grid min-h-0 flex-1 overflow-hidden lg:grid-cols-[minmax(0,1fr)_420px]">
              <div className="min-h-0 space-y-6 overflow-y-auto px-5 py-5 sm:px-6">
                {noMethods ? (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      No online payment method is set up for this unit yet.{' '}
                      <button type="button" className="font-medium underline" onClick={() => navigate(`/unit/${unitId}/settings?tab=payment`)}>
                        Set up payments in Unit Settings
                      </button>{' '}
                      to create a payment link.
                    </AlertDescription>
                  </Alert>
                ) : null}

                <Section n={1} title="Details">
                  <div>
                    <Label htmlFor="pl-title">Title</Label>
                    <Input
                      id="pl-title"
                      placeholder="e.g. Festival 2027 collection"
                      maxLength={TITLE_MAX_LENGTH}
                      value={input.title}
                      aria-invalid={!!err('title')}
                      onChange={(e) => set('title', e.target.value)}
                    />
                    <FieldError message={err('title')} />
                  </div>
                  <div>
                    <Label htmlFor="pl-description">
                      Description <span className="font-normal text-muted-foreground">(optional)</span>
                    </Label>
                    <Textarea
                      id="pl-description"
                      rows={3}
                      maxLength={DESCRIPTION_MAX_LENGTH}
                      placeholder="What the money is for"
                      value={input.description}
                      onChange={(e) => set('description', e.target.value)}
                    />
                    <p className="mt-1 text-right text-xs text-muted-foreground">
                      {input.description.length} / {DESCRIPTION_MAX_LENGTH}
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="pl-reference">Reference</Label>
                    <Input
                      id="pl-reference"
                      className="font-mono uppercase tracking-wide"
                      placeholder="FESTIVAL27"
                      value={input.reference}
                      aria-invalid={!!err('reference')}
                      aria-describedby="pl-reference-help"
                      onChange={(e) => set('reference', normalizeReference(e.target.value).slice(0, REFERENCE_MAX_LENGTH))}
                    />
                    <div id="pl-reference-help" className="mt-1 flex justify-between gap-2 text-xs text-muted-foreground">
                      <span>Appears on bank statements and in exports. Letters, numbers and - . / &amp;</span>
                      <span className="tabular-nums">
                        {normalizeReference(input.reference).length} / {REFERENCE_MAX_LENGTH}
                      </span>
                    </div>
                    <FieldError message={err('reference')} />
                  </div>
                  <div>
                    <Label htmlFor="pl-thanks">
                      Thank-you message <span className="font-normal text-muted-foreground">(optional)</span>
                    </Label>
                    <Textarea
                      id="pl-thanks"
                      rows={2}
                      maxLength={THANK_YOU_MAX_LENGTH}
                      placeholder="Shown after payment and in the receipt"
                      value={input.thankYouMessage}
                      onChange={(e) => set('thankYouMessage', e.target.value)}
                    />
                  </div>
                </Section>

                <Section n={2} title="Amount">
                  <div className="inline-flex w-full rounded-lg bg-muted p-1 sm:w-auto" role="radiogroup" aria-label="Amount type">
                    {AMOUNT_TYPES.map((t) => (
                      <button
                        key={t.value}
                        type="button"
                        role="radio"
                        aria-checked={input.amountType === t.value}
                        onClick={() => set('amountType', t.value)}
                        className={cn(
                          'min-h-[40px] flex-1 rounded-md px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:flex-none',
                          input.amountType === t.value ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                        )}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>

                  {input.amountType === 'fixed' ? (
                    <div className="max-w-xs">
                      <MoneyInput
                        id="pl-fixed"
                        label="Amount"
                        value={fixedText}
                        error={err('fixedAmountPence')}
                        onChange={(t) => {
                          setFixedText(t);
                          set('fixedAmountPence', parsePoundsInput(t));
                        }}
                      />
                    </div>
                  ) : null}
                  {input.amountType === 'range' ? (
                    <div className="flex max-w-md gap-3">
                      <MoneyInput
                        id="pl-min"
                        label="Minimum"
                        value={minText}
                        error={err('minAmountPence')}
                        onChange={(t) => {
                          setMinText(t);
                          set('minAmountPence', parsePoundsInput(t));
                        }}
                      />
                      <MoneyInput
                        id="pl-max"
                        label="Maximum"
                        value={maxText}
                        error={err('maxAmountPence')}
                        onChange={(t) => {
                          setMaxText(t);
                          set('maxAmountPence', parsePoundsInput(t));
                        }}
                      />
                    </div>
                  ) : null}
                  {input.amountType !== 'fixed' ? (
                    <div>
                      <Label htmlFor="pl-suggest">
                        Suggested amounts <span className="font-normal text-muted-foreground">(optional, up to {MAX_SUGGESTED_AMOUNTS})</span>
                      </Label>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        {input.suggestedAmountsPence.map((p) => (
                          <span key={p} className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-sm text-primary-foreground">
                            {formatPenceShort(p)}
                            <button
                              type="button"
                              aria-label={`Remove ${formatPenceShort(p)}`}
                              onClick={() => set('suggestedAmountsPence', input.suggestedAmountsPence.filter((x) => x !== p))}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                        {input.suggestedAmountsPence.length < MAX_SUGGESTED_AMOUNTS ? (
                          <div className="flex items-center gap-1">
                            <div className="relative w-24">
                              <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">£</span>
                              <Input
                                id="pl-suggest"
                                inputMode="decimal"
                                className="h-9 pl-5"
                                value={suggestText}
                                onChange={(e) => setSuggestText(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    addSuggestion();
                                  }
                                }}
                              />
                            </div>
                            <Button type="button" size="sm" variant="outline" onClick={addSuggestion}>
                              Add
                            </Button>
                          </div>
                        ) : null}
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">Shown to payers as quick picks.</p>
                      <FieldError message={err('suggestedAmountsPence')} />
                    </div>
                  ) : null}
                  {changedPricing ? <p className="text-sm text-amber-700 dark:text-amber-400">Changes apply to new payments only.</p> : null}
                </Section>

                <Section n={3} title="Payment & fees">
                  <div>
                    <p className="mb-2 text-sm font-medium">Payment method</p>
                    <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Payment method">
                      {PAYMENT_LINK_METHODS.map((m) => {
                        const available = setup.availableMethods.includes(m) || (editing && link?.paymentMethod === m);
                        const Icon = METHOD_ICONS[m];
                        const selected = input.paymentMethod === m;
                        if (!available) {
                          return (
                            <div key={m} className="flex flex-col items-start gap-1 rounded-lg border border-dashed border-border p-3 text-left opacity-70">
                              <span className="flex items-center gap-2 text-sm font-semibold">
                                <Icon className="h-4 w-4" />
                                {METHOD_LABELS[m]}
                              </span>
                              <span className="text-xs text-muted-foreground">{METHOD_DESCRIPTIONS[m]}</span>
                              <button
                                type="button"
                                className="text-xs font-medium text-primary underline"
                                onClick={() => navigate(`/unit/${unitId}/settings?tab=payment`)}
                              >
                                Set up in Unit Settings →
                              </button>
                            </div>
                          );
                        }
                        return (
                          <button
                            key={m}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            onClick={() => set('paymentMethod', m)}
                            className={cn(
                              'flex min-h-[44px] flex-col items-start gap-1 rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                              selected ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border hover:border-primary/50',
                            )}
                          >
                            <span className="flex w-full items-center justify-between gap-2 text-sm font-semibold">
                              <span className="flex items-center gap-2">
                                <Icon className="h-4 w-4" />
                                {METHOD_LABELS[m]}
                              </span>
                              {selected ? <Check className="h-4 w-4 text-primary" /> : null}
                            </span>
                            <span className="text-xs text-muted-foreground">{METHOD_DESCRIPTIONS[m]}</span>
                            <span className="text-xs text-muted-foreground">
                              {setup.feePence[m] > 0 ? `${formatPence(setup.feePence[m])} per payment` : 'No platform fee'}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    <FieldError message={err('paymentMethod')} />
                  </div>

                  <div>
                    <p className="mb-2 text-sm font-medium">Fee handling</p>
                    <div className="space-y-2" role="radiogroup" aria-label="Fee handling">
                      {(['on_top', 'included'] as const).map((mode) => (
                        <label
                          key={mode}
                          className={cn(
                            'flex cursor-pointer items-start gap-3 rounded-lg border p-3',
                            input.feeMode === mode ? 'border-primary bg-primary/5' : 'border-border',
                          )}
                        >
                          <input
                            type="radio"
                            name="pl-fee-mode"
                            className="mt-1 accent-[hsl(var(--primary))]"
                            checked={input.feeMode === mode}
                            onChange={() => set('feeMode', mode)}
                          />
                          <span>
                            <span className="block text-sm font-medium">{mode === 'on_top' ? 'Add fee on top' : 'Include fee in amount'}</span>
                            <span className="block text-xs text-muted-foreground" aria-live="polite">
                              {feeExampleText(mode, exampleAmount, feePence)}
                            </span>
                          </span>
                        </label>
                      ))}
                    </div>
                    <FieldError message={err('feeMode')} />
                  </div>
                </Section>

                <Section n={4} title="Gift Aid & payer details">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <Label htmlFor="pl-giftaid">Allow Gift Aid declarations</Label>
                      <p className="text-xs text-muted-foreground">
                        Only switch on if the money goes to a registered charity that can claim Gift Aid.
                      </p>
                    </div>
                    <Switch id="pl-giftaid" checked={input.giftAidEnabled} onCheckedChange={(v) => set('giftAidEnabled', v)} />
                  </div>
                  <p className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
                    Payers can always give anonymously or leave their name and email. Gift Aid is offered once they add both.
                  </p>
                </Section>

                <Section n={5} title="Availability & notifications">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-4">
                      <Label htmlFor="pl-noexpiry" className="flex items-center gap-2">
                        <CalendarClock className="h-4 w-4" /> No expiry
                      </Label>
                      <Switch
                        id="pl-noexpiry"
                        checked={noExpiry}
                        onCheckedChange={(v) => {
                          setNoExpiry(v);
                          set('expiresAt', v ? null : fromDateTimeLocalValue(expiryText));
                        }}
                      />
                    </div>
                    {!noExpiry ? (
                      <div className="space-y-2">
                        <Label htmlFor="pl-expiry">Stop taking payments at</Label>
                        <Input
                          id="pl-expiry"
                          type="datetime-local"
                          className="max-w-xs"
                          min={toDateTimeLocalValue(new Date())}
                          value={expiryText}
                          aria-invalid={!!err('expiresAt')}
                          onChange={(e) => {
                            setExpiryText(e.target.value);
                            set('expiresAt', fromDateTimeLocalValue(e.target.value));
                          }}
                        />
                        <div className="flex flex-wrap gap-2">
                          <Button type="button" size="sm" variant="outline" onClick={() => setExpiryFromDate(expiryPreset('today'))}>
                            End of today
                          </Button>
                          <Button type="button" size="sm" variant="outline" onClick={() => setExpiryFromDate(expiryPreset(7))}>
                            In 7 days
                          </Button>
                          <Button type="button" size="sm" variant="outline" onClick={() => setExpiryFromDate(expiryPreset(30))}>
                            In 30 days
                          </Button>
                        </div>
                        <FieldError message={err('expiresAt') || (!input.expiresAt && submitAttempted ? 'Choose an expiry date or switch on No expiry' : undefined)} />
                      </div>
                    ) : null}
                  </div>

                  <div className="space-y-2">
                    <Label>Notify unit leads</Label>
                    <Select
                      value={input.notifications.mode}
                      onValueChange={(v) => set('notifications', { ...input.notifications, mode: v as PaymentLinkConfigInput['notifications']['mode'] })}
                    >
                      <SelectTrigger className="max-w-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="each">Every payment</SelectItem>
                        <SelectItem value="daily">Daily summary</SelectItem>
                        <SelectItem value="off">Off</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">Emails go to the Secretary, the Treasurer and whoever created the link.</p>
                  </div>
                  <div>
                    <Label>
                      Extra recipients <span className="font-normal text-muted-foreground">(optional)</span>
                    </Label>
                    <TagInput
                      tags={input.notifications.extraRecipients}
                      onChange={(tags) => set('notifications', { ...input.notifications, extraRecipients: tags.map((t) => t.trim().toLowerCase()) })}
                      placeholder="Type an email and press Enter"
                    />
                    <FieldError message={err('extraRecipients')} />
                  </div>
                  {!noExpiry ? (
                    <div className="flex items-center justify-between gap-4">
                      <Label htmlFor="pl-reminder">Remind me 3 days before expiry</Label>
                      <Switch
                        id="pl-reminder"
                        checked={input.notifications.expiryReminder}
                        onCheckedChange={(v) => set('notifications', { ...input.notifications, expiryReminder: v })}
                      />
                    </div>
                  ) : null}
                </Section>

                {/* Mobile preview */}
                <div className="lg:hidden">
                  <Button type="button" variant="outline" className="w-full justify-between" onClick={() => setShowMobilePreview((v) => !v)} aria-expanded={showMobilePreview}>
                    <span className="flex items-center gap-2">
                      <Eye className="h-4 w-4" /> Preview
                    </span>
                    <ChevronDown className={cn('h-4 w-4 transition-transform', showMobilePreview && 'rotate-180')} />
                  </Button>
                  {showMobilePreview ? <div className="mt-3">{preview}</div> : null}
                </div>
              </div>

              <aside className="hidden min-h-0 overflow-y-auto border-l border-border bg-muted/30 p-5 lg:block" aria-label="Preview">
                <p className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <Eye className="h-3.5 w-3.5" /> What payers will see
                </p>
                {preview}
              </aside>
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-border px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving || noMethods || setup.loading}>
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {editing ? 'Save changes' : 'Create link'}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SuccessStep({
  unitName,
  title,
  reference,
  success,
  retrying,
  onRetry,
  onDone,
  input,
}: {
  unitName: string;
  title: string;
  reference: string;
  success: { linkId: string; shortUrl: string; shortenerUsed: boolean };
  retrying: boolean;
  onRetry: () => void;
  onDone: () => void;
  input: PaymentLinkConfigInput;
}) {
  const qr = useQrPreview(success.shortUrl, 480);
  const pseudoLink = {
    id: success.linkId,
    title,
    reference,
    shortUrl: success.shortUrl,
    description: input.description,
    amountType: input.amountType,
    fixedAmountPence: input.fixedAmountPence,
    minAmountPence: input.minAmountPence,
    maxAmountPence: input.maxAmountPence,
    paymentMethod: input.paymentMethod,
  } as PaymentLink;
  const { busy, downloadPng, downloadSvg } = usePaymentLinkDownloads(pseudoLink, unitName);
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const shareText = `${title}: ${success.shortUrl}`;

  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-5 py-6 sm:px-6">
      <div className="mx-auto grid max-w-3xl items-center gap-6 md:grid-cols-[1fr_240px]">
        <div className="space-y-4">
          <div>
            <Label>Payment link</Label>
            <div className="mt-1 flex gap-2">
              <code className="flex-1 break-all rounded-md border border-border bg-muted px-3 py-2 font-mono text-lg">{success.shortUrl}</code>
              <Button onClick={() => copyLink(success.shortUrl)} aria-label="Copy link">
                <Copy className="mr-2 h-4 w-4" /> Copy
              </Button>
            </div>
          </div>
          {!success.shortenerUsed ? (
            <p className="flex flex-wrap items-center gap-2 text-sm text-amber-700 dark:text-amber-400">
              <AlertTriangle className="h-4 w-4" /> Short link unavailable — using the full link.
              <Button size="sm" variant="ghost" onClick={onRetry} disabled={retrying}>
                {retrying ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <RefreshCw className="mr-1 h-3 w-3" />}Retry
              </Button>
            </p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            {canShare ? (
              <Button variant="outline" onClick={() => navigator.share({ title, text: title, url: success.shortUrl }).catch(() => undefined)}>
                <Share2 className="mr-2 h-4 w-4" /> Share
              </Button>
            ) : (
              <>
                <a
                  className={buttonVariants({ variant: 'outline' })}
                  href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp
                </a>
                <a className={buttonVariants({ variant: 'outline' })} href={`mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(shareText)}`}>
                  <Mail className="mr-2 h-4 w-4" /> Email
                </a>
              </>
            )}
            <Button variant="outline" onClick={downloadPng} disabled={!!busy}>
              <Download className="mr-2 h-4 w-4" /> QR PNG
            </Button>
            <Button variant="outline" onClick={downloadSvg} disabled={!!busy}>
              <Download className="mr-2 h-4 w-4" /> QR SVG
            </Button>
          </div>
        </div>
        <div className="mx-auto w-full max-w-[240px] rounded-lg border border-border bg-white p-2">
          {qr ? <img src={qr} alt={`QR code for ${title}`} className="w-full" /> : <div className="flex aspect-square items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}
        </div>
      </div>
      <div className="mt-8 flex justify-end">
        <Button onClick={onDone}>Done</Button>
      </div>
    </div>
  );
}
